import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Row, Spinner, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import PageLoader from "../components/PageLoader";
import TomSelect from "tom-select";
import { documentsApi, viewUrl, downloadUrl } from "../api/documents";
import { categoriesApi } from "../api/categories";
import { sourcesApi } from "../api/sources";

export default function DocumentForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const categorySelectRef = useRef(null);
  const sourceSelectRef = useRef(null);
  const categoryTomRef = useRef(null);
  const sourceTomRef = useRef(null);
  const localObjectUrlRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    category_id: "",
    source_id: "",
    publication_year: "",
    is_featured: 0,
    is_published: 1,
    file_type: "pdf",
  });
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [file, setFile] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentFileSize, setCurrentFileSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  // --- État du modal d'aperçu ---
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  // ============================================================
  // Chargement initial
  // ============================================================
  useEffect(() => {
    Promise.all([categoriesApi.list(), sourcesApi.list()])
      .then(([cats, srcs]) => {
        setCategories(cats || []);
        setSources(srcs || []);
      })
      .catch((err) => console.error("Erreur chargement listes:", err));

    if (isEdit) {
      documentsApi
        .list()
        .then((docs) => {
          const found = (docs || []).find((d) => String(d.id) === String(id));
          if (found) {
            setForm({
              title: found.title || "",
              slug: found.slug || "",
              description: found.description || "",
              category_id: found.category?.id || "",
              source_id: found.source?.id || "",
              publication_year: found.publicationYear || "",
              is_featured: found.isFeatured ? 1 : 0,
              is_published: 1,
              file_type: found.fileType || "pdf",
            });
            // ✅ URL d'aperçu reconstruite via le helper (comme view_url() en PHP)
            setCurrentFile(viewUrl(found.slug));
            setCurrentFileSize(found.fileSize || null);
          }
        })
        .catch((err) => console.error("Erreur chargement document:", err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  // ============================================================
  // Tom Select — catégories
  // ============================================================
  useEffect(() => {
    if (loading || categories.length === 0 || sources.length === 0) return;

    if (categorySelectRef.current && !categoryTomRef.current) {
      categoryTomRef.current = new TomSelect(categorySelectRef.current, {
        create: false,
        maxItems: 1,
        allowEmptyOption: true,
        placeholder: "Rechercher une catégorie…",
        sortField: { field: "text", direction: "asc" },
        onChange: (value) => {
          setForm((f) => ({ ...f, category_id: value }));
        },
      });
      categoryTomRef.current.setValue(String(form.category_id || ""), true);
    }

    if (sourceSelectRef.current && !sourceTomRef.current) {
      sourceTomRef.current = new TomSelect(sourceSelectRef.current, {
        create: false,
        maxItems: 1,
        allowEmptyOption: true,
        placeholder: "Rechercher une source…",
        sortField: { field: "text", direction: "asc" },
        onChange: (value) => {
          setForm((f) => ({ ...f, source_id: value }));
        },
      });
      sourceTomRef.current.setValue(String(form.source_id || ""), true);
    }
  }, [loading, categories, sources]);

  useEffect(() => {
    if (categoryTomRef.current && form.category_id) {
      categoryTomRef.current.setValue(String(form.category_id), true);
    }
    if (sourceTomRef.current && form.source_id) {
      sourceTomRef.current.setValue(String(form.source_id), true);
    }
  }, [form.category_id, form.source_id]);

  // Nettoyage Tom Select + object URL au démontage
  useEffect(() => {
    return () => {
      if (categoryTomRef.current) categoryTomRef.current.destroy();
      if (sourceTomRef.current) sourceTomRef.current.destroy();
      if (localObjectUrlRef.current) {
        URL.revokeObjectURL(localObjectUrlRef.current);
        localObjectUrlRef.current = null;
      }
    };
  }, []);

  // ============================================================
  // Modal d'aperçu
  // ============================================================
  const openPreview = () => {
    if (!currentFile) return;
    setPreviewUrl(currentFile);
    setPreviewLoading(true);
    setPreviewOpen(true);
  };

  const openLocalPreview = () => {
    if (!file) return;
    // Nettoie l'ancien object URL s'il existe
    if (localObjectUrlRef.current) {
      URL.revokeObjectURL(localObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    localObjectUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
    setPreviewLoading(true);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setTimeout(() => {
      setPreviewUrl(null);
      setPreviewLoading(true);
      // Nettoie l'object URL si on était sur un aperçu local
      if (localObjectUrlRef.current) {
        URL.revokeObjectURL(localObjectUrlRef.current);
        localObjectUrlRef.current = null;
      }
    }, 300);
  };

  // ============================================================
  // Helpers formulaire
  // ============================================================
  const slugify = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm((f) => {
      const newForm = { ...f, title };
      if (!isEdit && (!f.slug || f.slug === slugify(f.title))) {
        newForm.slug = slugify(title);
      }
      return newForm;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // ============================================================
  // Upload fichier
  // ============================================================
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) validateAndSetFile(dropped);
  };

  const validateAndSetFile = (f) => {
    if (f.type !== "application/pdf") {
      setErrors((prev) => ({ ...prev, file: "Seuls les fichiers PDF sont acceptés." }));
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, file: "Fichier trop volumineux (max 50 Mo)." }));
      return;
    }
    setFile(f);
    setErrors((prev) => ({ ...prev, file: null }));
  };

  const handleFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) validateAndSetFile(f);
  };

  const removeSelectedFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (localObjectUrlRef.current) {
      URL.revokeObjectURL(localObjectUrlRef.current);
      localObjectUrlRef.current = null;
    }
  };

  // ============================================================
  // Validation + soumission
  // ============================================================
  const validateForm = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = "Le titre est requis.";
    if (!form.slug.trim()) errs.slug = "Le slug est requis.";
    if (form.slug && !/^[a-z0-9-]+$/.test(form.slug)) {
      errs.slug = "Le slug ne peut contenir que des lettres minuscules, chiffres et tirets.";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      let savedId = id;
      const payload = {
        ...form,
        publication_year: form.publication_year ? Number(form.publication_year) : null,
        category_id: form.category_id || null,
        source_id: form.source_id || null,
      };
      if (isEdit) {
        await documentsApi.update(id, payload);
      } else {
        const created = await documentsApi.create(payload);
        savedId = created.id;
      }
      if (file && savedId) {
        await documentsApi.uploadFile(savedId, file);
      }
      navigate("/documents");
    } catch (err) {
      alert("Erreur : " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Loader
  // ============================================================
  if (loading) {
    return (
      <React.Fragment>
        <Header onSkin={setSkin} />
        <div className="main main-app cere-main p-3 p-lg-4">
          <PageLoader message={isEdit ? "Chargement du document…" : "Préparation du formulaire…"} />
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // ============================================================
  // Rendu
  // ============================================================
  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">

        <div className="cere-page-head mb-4">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
              <li className="breadcrumb-item"><Link to="/documents">Documents</Link></li>
              <li className="breadcrumb-item active">{isEdit ? "Modifier" : "Nouveau"}</li>
            </ol>
          </nav>
          <h4 className="cere-page-title mb-0">
            {isEdit ? "Modifier le document" : "Nouveau document"}
          </h4>
          <p className="cere-page-subtitle mb-0">
            {isEdit
              ? "Mettez à jour les informations ou remplacez le fichier PDF."
              : "Ajoutez un nouveau document à la bibliothèque numérique."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="cere-form">
          <Row className="g-3">

            <Col lg="8">
              <Card className="cere-card mb-3">
                <Card.Header className="cere-card-header">
                  <Card.Title className="cere-card-title">
                    <i className="ri-file-text-line me-2"></i>
                    Informations du document
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col xs="12">
                      <label className="cere-form-label">
                        Titre <span className="cere-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={form.title}
                        onChange={handleTitleChange}
                        className={`cere-form-control ${errors.title ? "is-invalid" : ""}`}
                        placeholder="Ex : Constitution de la République gabonaise (2024)"
                      />
                      {errors.title && <div className="cere-form-error">{errors.title}</div>}
                    </Col>

                    <Col md="6">
                      <label className="cere-form-label">
                        Slug <span className="cere-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="slug"
                        value={form.slug}
                        onChange={handleChange}
                        className={`cere-form-control ${errors.slug ? "is-invalid" : ""}`}
                        placeholder="constitution-2024"
                      />
                      {errors.slug ? (
                        <div className="cere-form-error">{errors.slug}</div>
                      ) : (
                        <div className="cere-form-help">
                          Identifiant unique dans l'URL (lettres, chiffres, tirets)
                        </div>
                      )}
                    </Col>

                    <Col md="6">
                      <label className="cere-form-label">Année de publication</label>
                      <input
                        type="number"
                        name="publication_year"
                        value={form.publication_year}
                        onChange={handleChange}
                        className="cere-form-control"
                        placeholder="2024"
                        min="1900"
                        max="2100"
                      />
                    </Col>

                    <Col xs="12">
                      <label className="cere-form-label">Description</label>
                      <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows="4"
                        className="cere-form-control"
                        placeholder="Résumé du contenu du document…"
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="cere-card">
                <Card.Header className="cere-card-header">
                  <Card.Title className="cere-card-title">
                    <i className="ri-file-pdf-2-line me-2"></i>
                    Fichier PDF
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {/* Fichier actuel (édition) — bouton œil ouvre le modal */}
                  {isEdit && currentFile && !file && (
                    <div className="cere-file-current">
                      <div className="cere-file-current-icon">
                        <i className="ri-file-pdf-fill"></i>
                      </div>
                      <div className="cere-file-current-info">
                        <div className="cere-file-current-name">Fichier actuel</div>
                        <div className="cere-file-current-meta">
                          Le PDF est déjà uploadé. Déposez un nouveau fichier pour le remplacer.
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cere-icon-btn"
                        onClick={openPreview}
                        title="Aperçu du fichier actuel"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                    </div>
                  )}

                  {/* Fichier sélectionné — bouton œil ouvre l'aperçu local */}
                  {file && (
                    <div className="cere-file-selected">
                      <div className="cere-file-selected-icon">
                        <i className="ri-file-pdf-fill"></i>
                      </div>
                      <div className="cere-file-selected-info">
                        <div className="cere-file-selected-name">{file.name}</div>
                        <div className="cere-file-selected-meta">
                          {(file.size / 1024 / 1024).toFixed(2)} Mo
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cere-icon-btn"
                        onClick={openLocalPreview}
                        title="Aperçu du nouveau fichier"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                        type="button"
                        className="cere-icon-btn cere-icon-btn--danger"
                        onClick={removeSelectedFile}
                        title="Retirer ce fichier"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}

                  {!file && (
                    <div
                      className={`cere-dropzone ${dragOver ? "is-dragover" : ""}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="cere-dropzone-icon">
                        <i className="ri-upload-cloud-2-line"></i>
                      </div>
                      <div className="cere-dropzone-title">
                        Glissez-déposez votre PDF ici
                      </div>
                      <div className="cere-dropzone-desc">
                        ou <span className="cere-dropzone-link">parcourez vos fichiers</span>
                      </div>
                      <div className="cere-dropzone-hint">
                        PDF uniquement · 50 Mo maximum
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileInput}
                        style={{ display: "none" }}
                      />
                    </div>
                  )}

                  {errors.file && <div className="cere-form-error mt-2">{errors.file}</div>}
                </Card.Body>
              </Card>
            </Col>

            <Col lg="4">
              <Card className="cere-card mb-3">
                <Card.Header className="cere-card-header">
                  <Card.Title className="cere-card-title">
                    <i className="ri-price-tag-3-line me-2"></i>
                    Classement
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <label className="cere-form-label">Catégorie</label>
                  <select
                    ref={categorySelectRef}
                    className="cere-form-control mb-3"
                    defaultValue=""
                  >
                    <option value="">— Aucune catégorie —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>

                  <label className="cere-form-label">Source</label>
                  <select
                    ref={sourceSelectRef}
                    className="cere-form-control"
                    defaultValue=""
                  >
                    <option value="">— Aucune source —</option>
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </Card.Body>
              </Card>

              <Card className="cere-card mb-3">
                <Card.Header className="cere-card-header">
                  <Card.Title className="cere-card-title">
                    <i className="ri-settings-3-line me-2"></i>
                    Options
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="cere-toggle-row">
                    <div>
                      <div className="cere-toggle-label">Mettre en vedette</div>
                      <div className="cere-toggle-desc">Afficher en priorité sur la page d'accueil</div>
                    </div>
                    <label className="cere-switch">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={!!form.is_featured}
                        onChange={handleChange}
                      />
                      <span className="cere-switch-slider"></span>
                    </label>
                  </div>

                  <div className="cere-toggle-row">
                    <div>
                      <div className="cere-toggle-label">Publier le document</div>
                      <div className="cere-toggle-desc">Rendre visible sur le site public</div>
                    </div>
                    <label className="cere-switch">
                      <input
                        type="checkbox"
                        name="is_published"
                        checked={!!form.is_published}
                        onChange={handleChange}
                      />
                      <span className="cere-switch-slider"></span>
                    </label>
                  </div>
                </Card.Body>
              </Card>

              <Card className="cere-card cere-form-actions">
                <Card.Body>
                  <button
                    type="submit"
                    className="cere-btn cere-btn-primary w-100 mb-2"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Enregistrement…
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-2"></i>
                        {isEdit ? "Mettre à jour" : "Créer le document"}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="cere-btn cere-btn-outline w-100"
                    onClick={() => navigate("/documents")}
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </form>

        {/* ============================================================
            MODAL APERÇU PDF
            ============================================================ */}
        <Modal
          show={previewOpen}
          onHide={closePreview}
          centered
          size="xl"
          dialogClassName="cere-pdf-modal"
          backdrop="static"
          keyboard={true}
        >
          <div className="cere-pdf-modal__header">
            <div>
              <h5 className="cere-pdf-modal__title">
                {file ? file.name : form.title || "Aperçu du document"}
              </h5>
              <small className="cere-pdf-modal__subtitle">
                {file
                  ? `${(file.size / 1024 / 1024).toFixed(2)} Mo · Nouveau fichier (non enregistré)`
                  : "Aperçu du fichier actuel"}
              </small>
            </div>
            <button
              type="button"
              className="cere-pdf-modal__close"
              onClick={closePreview}
              aria-label="Fermer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="cere-pdf-modal__body">
            {previewLoading && (
              <div className="cere-pdf-modal__loading">
                <div className="cere-spinner"></div>
                <p>Chargement de l'aperçu…</p>
              </div>
            )}
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Aperçu du document"
                className="cere-pdf-modal__iframe"
                style={{ opacity: previewLoading ? 0 : 1 }}
                onLoad={() => setPreviewLoading(false)}
              />
            )}
          </div>

          <div className="cere-pdf-modal__footer">
            {isEdit && form.slug && !file && (
              <>
                <a
                  href={downloadUrl(form.slug)}
                  className="cere-btn cere-btn-outline"
                  download
                >
                  <i className="ri-download-line me-2"></i>
                  Télécharger
                </a>
                <a
                  href={currentFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cere-btn cere-btn-outline"
                >
                  <i className="ri-external-link-line me-2"></i>
                  Ouvrir dans un onglet
                </a>
              </>
            )}
            <button
              type="button"
              className="cere-btn cere-btn-primary"
              onClick={closePreview}
            >
              Fermer
            </button>
          </div>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}